import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageType } from '@vgtv/api-client/lib/page';

import { VGPaywallPopup } from '../components/Paywall/PaywallPopup/VGPaywallPopup';
import { Overlay } from '../components/Overlay/Overlay';
import { useAccessContext } from '../components/AccessProvider/AccessProvider';
import { hasAccess } from '../services/paywall';
import { getQueryStringParams } from '../utils/getQueryStringParams';
import { Button } from '../components/Button/Button';
import { getPurchaseUrl } from '../components/Paywall/PaywallPopup/helpers/getPurchaseUrl';
import { Spinner } from '../components/Spinner/Spinner';
import { usePageViewTracking } from '../components/utils/usePageViewTracking';

import * as styles from './PaywallPage.module.scss';

const PAYWALL_ACCESS = 'sport';
const DEFAULT_ENTITY_ID = '283331';
const PAYWALL_TYPE_DEFAULT = 'default';
const PAYWALL_TYPE_FALLBACK = 'fallback';

export const PaywallPage = () => {
  const [type, setType] = useState(PAYWALL_TYPE_DEFAULT);
  const { accesses, isFetched, isFetching } = useAccessContext();
  const params = getQueryStringParams<'entityId' | 'redirectUrl'>();
  const { t } = useTranslation();

  usePageViewTracking({
    pageType: PageType.CUSTOM,
    entity: {
      slug: 'paywall',
    },
  });

  const { redirectUrl, entityId = DEFAULT_ENTITY_ID } = params;

  const onDefaultPaywallLoadFailure = useCallback(() => {
    setType(PAYWALL_TYPE_FALLBACK);
  }, []);

  let popupContent;

  if (type === PAYWALL_TYPE_FALLBACK) {
    const subscriptionUrl = getPurchaseUrl(String(entityId), {
      code: 'VGS-1M129-E',
      content_id: 'fallback',
      state: redirectUrl,
    });

    popupContent = (
      <Button
        size="large"
        component="a"
        variant="contained"
        isActiveBrandColor
        href={subscriptionUrl}
      >
        {t('Subscribe to sport')}
      </Button>
    );
  } else {
    if (isFetching) {
      popupContent = <Spinner size="small" />;
    } else {
      if (isFetched && hasAccess(PAYWALL_ACCESS, accesses)) {
        popupContent = <div>{t('You already have this subscription')}</div>;
      } else {
        popupContent = (
          <VGPaywallPopup
            entityId={entityId}
            access={PAYWALL_ACCESS}
            section="sport"
            onLoadFailure={onDefaultPaywallLoadFailure}
            isOpen
            redirectUrl={redirectUrl}
          />
        );
      }
    }
  }

  return (
    <Overlay
      type="overlay/popup"
      forceRender
      isOpen={true}
      className={styles.popup}
      contentClassName={styles.popupContent}
    >
      {popupContent}
    </Overlay>
  );
};
